import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { label: "Home", id: "#hero", testid: "nav-link-home" },
    { label: "Services", id: "#services", testid: "nav-link-services" },
    { label: "How It Works", id: "#how-it-works", testid: "nav-link-how-it-works" },
    { label: "Why Reno Genie", id: "#why-reno-genie", testid: "nav-link-why-reno-genie" },
    { label: "Contact", id: "#contact", testid: "nav-link-contact" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 32);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-brand-teal/10 bg-white/75 shadow-[0_10px_40px_rgba(2,27,30,0.07)] backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
                <button
                    type="button"
                    onClick={() => go("#hero")}
                    data-testid="header-logo"
                    className="flex items-center gap-2 outline-none transition-transform duration-300 hover:scale-[1.03]"
                    aria-label="Reno Genie home"
                >
                    <img src="/brand/logo-horizontal.png" alt="Reno Genie" className="h-9 w-auto sm:h-10" />
                </button>

                <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
                    {LINKS.map((link) => (
                        <button
                            key={link.id}
                            type="button"
                            onClick={() => go(link.id)}
                            data-testid={link.testid}
                            className="group relative text-sm font-medium text-brand-deep/80 transition-colors duration-300 hover:text-brand-teal"
                        >
                            {link.label}
                            <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 rounded-full bg-brand-gold transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => go("#cta")}
                        data-testid="header-cta-button"
                        className="group hidden items-center gap-2 rounded-full bg-brand-teal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-deep hover:shadow-[0_14px_30px_rgba(6,78,87,0.3)] sm:inline-flex"
                    >
                        Get Started
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-button"
                        aria-label="Toggle menu"
                        aria-expanded={open}
                        className="relative flex h-11 w-11 items-center justify-center rounded-full border border-brand-teal/15 bg-white/70 backdrop-blur lg:hidden"
                    >
                        <span
                            className={`absolute h-[2px] w-5 rounded bg-brand-deep transition-all duration-300 ${
                                open ? "rotate-45" : "-translate-y-[4px]"
                            }`}
                        />
                        <span
                            className={`absolute h-[2px] w-5 rounded bg-brand-deep transition-all duration-300 ${
                                open ? "-rotate-45" : "translate-y-[4px]"
                            }`}
                        />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="mobile-menu"
                        className="border-t border-brand-teal/10 bg-white/95 px-6 pb-8 pt-4 backdrop-blur-xl lg:hidden"
                    >
                        {LINKS.map((link, i) => (
                            <motion.button
                                key={link.id}
                                type="button"
                                initial={{ opacity: 0, x: -14 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.05 * i, duration: 0.3 }}
                                onClick={() => go(link.id)}
                                data-testid={`mobile-${link.testid}`}
                                className="block w-full border-b border-brand-line py-4 text-left font-display text-2xl text-brand-deep transition-colors hover:text-brand-teal"
                            >
                                {link.label}
                            </motion.button>
                        ))}
                        <button
                            type="button"
                            onClick={() => go("#cta")}
                            data-testid="mobile-header-cta-button"
                            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-bold text-brand-deep"
                        >
                            Get Started
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
